import React, { useState } from 'react';
import {
	attachDiscourseTravel,
	attachToDiscourse,
	deleteDiscourseAttachment,
	discardDiscourse,
	initializeNewDiscourse,
	prepareDefaultValues,
	prepareDiscourseItems,
	prepareSaveDiscourseItems,
	saveDiscourse,
	stashDiscourse,
	attachPrescription,
	updateDiscourseTravel,
} from './discourse.service';
import {
	DiscourseTravelFormValues,
	findDiscourseTravelAttachment,
} from '../Components/DiscourseTravel/discourseTravel.helpers';
import useApiClient from '../../Base/Hooks/useApiClient';
import {
	DocumentItem,
	useLazyAttachmentDetailQuery,
	useLazyDiscourseQuery,
	useLazyDraftDiscourseQuery,
} from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import Loading from '../../../Components/Loading';
import usePatientMeta from '../../../Components/PatientProfile/Hooks/usePatientMeta';
import apiClient from '../../../Services/ApiClient';
import usePatientDiagnosisMeta from '../../../Components/PatientProfile/Hooks/usePatientDiagnosisMeta';

export interface DiscourseValues {
	name?: string;
	text?: string;
	date?: string;
	time?: any;
	isDatetimeManual?: boolean;
	diagnosis?: any;
	diagnoses?: any[];
	discourseId: string;
}

export interface DiscourseContextProps {
	activeDiscourseId?: string;
	setActiveDiscourseId: (newState: string) => void;
	isDiscourseSaving: boolean;
	setIsDiscourseSaving: (newState: boolean) => void;
	handleSaveDiscourse: () => Promise<boolean>;
	removeAttachmentFromDiscourse: (
		attachmentId: string,
		soft?: boolean,
	) => Promise<void>;
	handleStashDiscourse: (newState: DiscourseValues) => void;
	handleDiscardActiveDiscourse: () => void;
	handleRefreshAttachments: (soft?: boolean) => Promise<void>;
	draftDiscourseValues: DiscourseValues | undefined;
	isLoading: boolean;
	setDiscourseValues: (newState: DiscourseValues | undefined) => void;
	discourseValues?: DiscourseValues;
	discourseAttachments?: DocumentItem[];
	handleEditDiscourseRecord: (
		discourseId: string,
		createNewVersion?: boolean,
		skipLoading?: boolean,
	) => Promise<void>;
	handleAttachToDiscourse: (attachmentId: string, type: string) => void;
	handleAddCustomAttachmentToDiscourse: ({
		attachmentId,
		type,
		attachmentTitle,
		sendToPatient,
		folderId,
		soft,
	}: {
		attachmentId: string;
		type: string;
		attachmentTitle: string;
		sendToPatient: boolean;
		folderId?: string;
		soft?: boolean;
	}) => Promise<void>;
	handleAttachPrescription: (prescriptionId: string, title?: string) => void;
	handleSaveDiscourseTravel: (
		values: DiscourseTravelFormValues,
	) => Promise<void>;
}

export interface DiscourseContextProviderProps {
	children?: React.ReactChild | React.ReactChild[];
	patientId: string;
	virtualId: string;
	workplaceId: string;
	loggedUserId: string;
}

export const DiscourseContext = React.createContext<DiscourseContextProps>({
	activeDiscourseId: undefined,
	isDiscourseSaving: false,
	draftDiscourseValues: { discourseId: '' },
	isLoading: false,
	discourseValues: undefined,
	discourseAttachments: undefined,
	setActiveDiscourseId: () => {},
	setIsDiscourseSaving: () => {},
	handleSaveDiscourse: async () => false,
	removeAttachmentFromDiscourse: async () => {},
	handleStashDiscourse: () => {},
	handleDiscardActiveDiscourse: () => {},
	handleRefreshAttachments: async () => {},
	setDiscourseValues: () => {},
	handleEditDiscourseRecord: async () => {},
	handleAttachToDiscourse: () => {},
	handleAddCustomAttachmentToDiscourse: async () => {},
	handleAttachPrescription: () => {},
	handleSaveDiscourseTravel: async () => {},
});

export const useDiscourseContext: () => DiscourseContextProps = () => {
	return React.useContext<DiscourseContextProps>(DiscourseContext);
};

export function DiscourseContextProvider({
	children,
	patientId,
	virtualId,
	workplaceId,
	loggedUserId,
}: DiscourseContextProviderProps) {
	const { displayError } = useApiClient();
	const { reFetchMeta, patientDiagnosis, metaIsLoading } =
		usePatientDiagnosisMeta({
			original: {
				id: patientId,
			},
			virtual: {
				id: virtualId,
			},
		});
	const [getDraftDiscourseData] = useLazyDraftDiscourseQuery();
	const [getDiscourseData] = useLazyDiscourseQuery();
	const [getAttachmentData] = useLazyAttachmentDetailQuery();
	const [activeDiscourseId, setActiveDiscourseId] = useState<
		string | undefined
	>();
	const [isDiscourseSaving, setIsDiscourseSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [draftDiscourseValues, setDraftDiscourseValues] = useState<
		DiscourseValues | undefined
	>(undefined);
	const [discourseValues, setDiscourseValues] = useState<
		DiscourseValues | undefined
	>(undefined);
	const [updateTimer, setUpdateTimer] = useState<null | number>(null);
	const [retryTimer, setRetryTimer] = useState<null | number>(null);
	const [discourseAttachments, setDiscourseAttachments] = useState<
		DocumentItem[]
	>([]);
	const [editMode, setEditMode] = useState(false);

	useEffectAsync(async () => {
		if (!editMode) {
			await getDraftDiscourse();
		}
	}, [activeDiscourseId]);

	const stashDiscourseWithTimeout = (
		values: DiscourseValues,
		triggerByRetry: boolean = false,
	) => {
		if (updateTimer !== null) {
			clearTimeout(updateTimer);
		}
		if (triggerByRetry || !isDiscourseSaving) {
			// @ts-ignore
			setUpdateTimer(
				// @ts-ignore
				setTimeout(async () => {
					setIsDiscourseSaving(true);
					await handleStashDiscourse(values);
					setTimeout(async () => {
						setIsDiscourseSaving(false);
					}, 1500);
				}, 500),
			);
		} else {
			if (retryTimer !== null) {
				clearTimeout(retryTimer);
			}
			setRetryTimer(
				// @ts-ignore
				setTimeout(() => {
					stashDiscourseWithTimeout(values, true);
				}, 500),
			);
		}
	};

	useEffectAsync(async () => {
		if (discourseValues) {
			stashDiscourseWithTimeout(discourseValues);
		}
	}, [discourseValues]);

	const getDraftDiscourse = async (soft?: boolean) => {
		if (!soft) {
			setIsLoading(true);
		}
		const data = await getDraftDiscourseData({ patientId }).unwrap();
		if (data?.document) {
			setActiveDiscourseId(data?.document?.id);
			setDraftDiscourseValues(prepareDefaultValues(data?.document));
			setDiscourseAttachments(data?.document?.items as DocumentItem[]);
		} else {
			if (!activeDiscourseId) {
				await handleNewDiscourseInitialization();
			}
		}
		setIsLoading(false);
	};

	const handleNewDiscourseInitialization = async () => {
		if (!metaIsLoading) {
			const newDiscourse = await initializeNewDiscourse(
				workplaceId,
				patientId,
				loggedUserId,
				prepareDiscourseItems(
					{
						discourseId: '',
						diagnosis: patientDiagnosis?.mainDiagnosis,
						diagnoses: patientDiagnosis?.secondaryDiagnoses?.map(
							(item: any) => ({ secondaryDiagnosis: item }),
						),
					},
					true,
				),
			);
			setActiveDiscourseId(newDiscourse?.data?.id);
		}
	};

	const handleRefreshAttachments = async (soft?: boolean) => {
		if (editMode && activeDiscourseId) {
			await handleEditDiscourseRecord(activeDiscourseId, false, true);
		} else {
			await getDraftDiscourse(soft);
		}
	};

	const handleSaveDiscourse = async (): Promise<boolean> => {
		if (!activeDiscourseId) {
			return false;
		}

		if (updateTimer !== null) {
			clearTimeout(updateTimer);
		}
		if (retryTimer !== null) {
			clearTimeout(retryTimer);
		}

		setIsLoading(true);
		try {
			if (discourseValues?.discourseId === activeDiscourseId) {
				await stashDiscourse(
					activeDiscourseId,
					prepareSaveDiscourseItems(discourseValues),
				);
			}
			const savedDiscourse = await saveDiscourse(activeDiscourseId);
			if (savedDiscourse) {
				if (editMode) {
					setEditMode(false);
				}
				setDiscourseAttachments([]);
				await reFetchMeta();
				await getDraftDiscourse();
				setActiveDiscourseId(undefined);
				return true;
			}
		} catch (e: any) {
			displayError(e);
		} finally {
			setIsLoading(false);
		}

		return false;
	};

	const removeAttachmentFromDiscourse = async (
		attachmentId: string,
		soft?: boolean,
	) => {
		if (activeDiscourseId) {
			await deleteDiscourseAttachment(activeDiscourseId, attachmentId);
		}
		await handleRefreshAttachments(soft);
	};

	const handleStashDiscourse = async (values: DiscourseValues) => {
		if (!activeDiscourseId) {
			return;
		}
		if (values?.discourseId === activeDiscourseId) {
			const response = await stashDiscourse(
				activeDiscourseId,
				prepareDiscourseItems(values),
			);
		}
	};

	const handleDiscardActiveDiscourse = async () => {
		if (activeDiscourseId) {
			setIsLoading(true);
			try {
				const savedDiscourse = await discardDiscourse(activeDiscourseId);
				if (savedDiscourse) {
					setActiveDiscourseId(undefined);
					setDiscourseAttachments([]);
					await getDraftDiscourse();
				}
			} catch (e: any) {
				displayError(e);
			}
			setIsLoading(false);
			setEditMode(false);
		}
	};

	const createNewDiscourseVersion = async (discourseId: string) => {
		const newVersion = await apiClient.get(`/document/${discourseId}/copy`);
		return newVersion?.data?.id;
	};

	const handleEditDiscourseRecord = async (
		discourseId: string,
		createNewVersion: boolean = true,
		skipLoading: boolean = false,
	) => {
		const showLoading = !createNewVersion && !skipLoading;
		if (showLoading) {
			setIsLoading(true);
		}
		try {
			const id: string = createNewVersion
				? await createNewDiscourseVersion(discourseId)
				: discourseId;
			if (id) {
				const data = await getDiscourseData({
					id,
				}).unwrap();
				if (data?.document) {
					setEditMode(true);
					setActiveDiscourseId(data?.document?.id);
					setDraftDiscourseValues(prepareDefaultValues(data?.document));
					setDiscourseAttachments(data?.document?.items as DocumentItem[]);
				}
			}
		} finally {
			if (showLoading) {
				setIsLoading(false);
			}
		}
	};

	const handleAttachToDiscourse = async (
		attachmentId: string,
		type: string,
	) => {
		if (activeDiscourseId) {
			const attachmentData = await getAttachmentData({
				id: attachmentId,
			}).unwrap();
			if (attachmentData?.document?.items) {
				const attachmentTitle =
					attachmentData?.document?.items?.find(item => item?.type === 'title')
						?.extra?.text || null;
				const attachmentTemplate =
					attachmentData?.document?.items?.find(
						item => item?.type === 'pdfTemplateData',
					)?.extra?.text || null;
				try {
					await attachToDiscourse(
						activeDiscourseId,
						attachmentId,
						type,
						attachmentTitle,
						attachmentTemplate?.extra?.pdfData?.rawData?.sendToPatient,
					);
				} catch (e: any) {
					displayError(e);
				}
			}
		}
		await handleRefreshAttachments();
	};

	const handleAddCustomAttachmentToDiscourse = async ({
		attachmentId,
		type,
		attachmentTitle,
		sendToPatient,
		folderId,
		soft,
	}: {
		attachmentId: string;
		type: string;
		attachmentTitle: string;
		sendToPatient: boolean;
		folderId?: string;
		soft?: boolean;
	}) => {
		if (activeDiscourseId) {
			try {
				await attachToDiscourse(
					activeDiscourseId,
					attachmentId,
					type,
					attachmentTitle,
					sendToPatient,
					folderId,
				);
			} catch (e: any) {
				displayError(e);
			}
		}
		await handleRefreshAttachments(soft);
	};

	const handleAttachPrescription = async (
		prescriptionId: string,
		title?: string,
	) => {
		if (activeDiscourseId && prescriptionId) {
			await attachPrescription(activeDiscourseId, prescriptionId, title);
		}

		await handleRefreshAttachments();
	};

	const handleSaveDiscourseTravel = async (
		values: DiscourseTravelFormValues,
	) => {
		if (!activeDiscourseId) {
			return;
		}

		const extra = {
			distanceKm: Number(values.distanceKm),
			fromAddress: values.fromAddress,
			toAddress: values.toAddress,
		};
		const existingTravel = findDiscourseTravelAttachment(discourseAttachments);

		try {
			if (existingTravel?.id) {
				await updateDiscourseTravel(
					activeDiscourseId,
					existingTravel.id,
					extra,
				);
			} else {
				await attachDiscourseTravel(activeDiscourseId, extra);
			}
		} catch (e: any) {
			displayError(e);
			throw e;
		}

		await handleRefreshAttachments(true);
	};

	return (
		<DiscourseContext.Provider
			value={{
				activeDiscourseId,
				isDiscourseSaving,
				draftDiscourseValues,
				isLoading,
				discourseValues,
				discourseAttachments,
				setActiveDiscourseId,
				setIsDiscourseSaving,
				handleSaveDiscourse,
				removeAttachmentFromDiscourse,
				handleStashDiscourse,
				handleDiscardActiveDiscourse,
				handleRefreshAttachments,
				setDiscourseValues,
				handleEditDiscourseRecord,
				handleAttachToDiscourse,
				handleAddCustomAttachmentToDiscourse,
				handleAttachPrescription,
				handleSaveDiscourseTravel,
			}}>
			{(isLoading || metaIsLoading) && <Loading />}
			{!isLoading && !metaIsLoading && children}
		</DiscourseContext.Provider>
	);
}
